import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { pettyCashAPI, userAPI, purchaseOrderAPI, propertyAPI, vendorAPI, clientAPI, getBillUrl, downloadBill, viewBill } from '../lib/api';
import { compressImageFile } from '../lib/imageCompress';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { SpellCheckInput } from '../components/SpellCheckInput';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { 
  Wallet,
  Send,
  Receipt,
  RotateCcw,
  CheckCircle,
  XCircle,
  Clock,
  Plus,
  Loader2,
  IndianRupee,
  User,
  FileText,
  Upload,
  Eye,
  AlertTriangle,
  Building2,
  Truck,
  Download,
  Users,
  Pencil,
  BarChart3,
  TrendingUp,
  Calendar,
  X,
  Trash2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  RotateCw,
  Link2 as LinkIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, formatDate } from '../lib/utils';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';

export const PettyCashPage = () => {
  const [loading, setLoading] = useState(true);
  const [firstLoad, setFirstLoad] = useState(true);
  const [activeTab, setActiveTab] = useState('transactions');
  const [dashboard, setDashboard] = useState(null);
  const [myBalance, setMyBalance] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvalStatusFilter, setApprovalStatusFilter] = useState('pending');
  const [approvalMemberFilter, setApprovalMemberFilter] = useState('all');
  const [approvalSort, setApprovalSort] = useState({ key: 'date', dir: 'desc' });
  const [users, setUsers] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [properties, setProperties] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [clients, setClients] = useState([]);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [expensesWithoutBills, setExpensesWithoutBills] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingExpenseId, setUploadingExpenseId] = useState(null);
  const [showNoBillsExpanded, setShowNoBillsExpanded] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const fileInputRef = useRef(null);
  const existingBillInputRef = useRef(null);
  const editBillInputRef = useRef(null);
  
  // Dialog states
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [expenseDialogOpen, setExpenseDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkRecipient, setBulkRecipient] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [memberTransfersOpen, setMemberTransfersOpen] = useState(false);
  const [memberTransfers, setMemberTransfers] = useState(null);
  const [memberTransfersLoading, setMemberTransfersLoading] = useState(false);
  const [deleteTransferId, setDeleteTransferId] = useState(null);
  const [selectedTransferIds, setSelectedTransferIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  
  // Form states
  const [transferForm, setTransferForm] = useState({ recipient_user_id: '', amount: '', notes: '', transfer_date: new Date().toISOString().split('T')[0] });
  const [expenseForm, setExpenseForm] = useState({
    amount: '',
    description: '',
    expense_date: new Date().toISOString().split('T')[0],
    property_id: '',
    vendor_id: '',
    po_id: '',
    client_id: '',
    category: '',
    other_category: '',
    bill_number: '',
    bill_images: [],  // Changed to array for multiple bills
    purchased_by: '',
    notes: ''
  });
  const [returnForm, setReturnForm] = useState({ amount: '', notes: '' });
  const [rejectionReason, setRejectionReason] = useState('');
  const [reportFilters, setReportFilters] = useState({
    start_date: '',
    end_date: '',
    user_id: '',
    client_id: '',
    category: '',
    status: ''
  });
  
  // Bulk selection state for pending approvals
  const [selectedApprovals, setSelectedApprovals] = useState([]);
  const [bulkApproving, setBulkApproving] = useState(false);
  
  // Edit expense state
  const [editExpenseDialogOpen, setEditExpenseDialogOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [editingTransfer, setEditingTransfer] = useState(null);
  const [editExpenseForm, setEditExpenseForm] = useState({
    amount: '',
    description: '',
    expense_date: '',
    property_id: '',
    vendor_id: '',
    po_id: '',
    client_id: '',
    category: '',
    other_category: '',
    bill_number: '',
    bill_images: [],
    purchased_by: '',
    notes: ''
  });

  // Check if current user is admin (simplified - in real app, get from auth context)
  const [isAdmin, setIsAdmin] = useState(true);
  const { user } = useAuth();
  const [rejectedBannerDismissed, setRejectedBannerDismissed] = useState(false);
  
  // Sorting state for transactions
  const [sortBy, setSortBy] = useState('recorded_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [filterMember, setFilterMember] = useState('all');
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [entryToDelete, setEntryToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Deleted entries view (for recovery)
  const [showDeletedEntries, setShowDeletedEntries] = useState(false);
  const [deletedEntries, setDeletedEntries] = useState([]);
  const [loadingDeleted, setLoadingDeleted] = useState(false);
  
  // Analytics/Reports state
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [reportDateRange, setReportDateRange] = useState({
    start_date: '',
    end_date: ''
  });
  
  // Chart colors
  const CHART_COLORS = ['#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#6366f1', '#14b8a6', '#f97316', '#84cc16'];

  useEffect(() => {
    fetchData();
  }, [sortBy, sortOrder, filterMember]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const txParams = { sort_by: sortBy, sort_order: sortOrder };
      if (filterMember && filterMember !== 'all') {
        txParams.user_id = filterMember;
      }
      const [dashboardRes, balanceRes, transactionsRes, usersRes, posRes, categoriesRes, propertiesRes, vendorsRes, noBillsRes, clientsRes] = await Promise.all([
        pettyCashAPI.getDashboard().catch(() => ({ data: null })),
        pettyCashAPI.getMyBalance(),
        pettyCashAPI.getTransactions(txParams),
        userAPI.getAll({ status: 'active' }),
        purchaseOrderAPI.getAll(),
        pettyCashAPI.getExpenseCategories(),
        propertyAPI.getAll({ status: 'active' }),
        vendorAPI.getAll({ status: 'active' }),
        pettyCashAPI.getExpensesWithoutBills().catch(() => ({ data: { count: 0, expenses: [] } })),
        clientAPI.getAll().catch(() => ({ data: [] }))
      ]);
      
      setDashboard(dashboardRes.data);
      setMyBalance(balanceRes.data);
      setTransactions(transactionsRes.data);
      setUsers(usersRes.data);
      setPurchaseOrders(posRes.data.filter(po => po.status !== 'cancelled'));
      setExpenseCategories(categoriesRes.data);
      setProperties(propertiesRes.data);
      setVendors(vendorsRes.data);
      setExpensesWithoutBills(noBillsRes.data);
      setClients(clientsRes.data || []);
      
      // Fetch pending approvals if admin
      if (dashboardRes.data) {
        const pendingRes = await pettyCashAPI.getPendingApprovals(approvalStatusFilter).catch(() => ({ data: [] }));
        setPendingApprovals(pendingRes.data);
        setIsAdmin(true);
      } else {
        setIsAdmin(false);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load petty cash data');
    } finally {
      setLoading(false);
      setFirstLoad(false);
    }
  };

  // Refetch approvals when the status filter changes (admins only)
  const refetchApprovals = async (statusVal) => {
    try {
      const res = await pettyCashAPI.getPendingApprovals(statusVal).catch(() => ({ data: [] }));
      setPendingApprovals(res.data);
      setSelectedApprovals([]);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (isAdmin) {
      refetchApprovals(approvalStatusFilter);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvalStatusFilter]);

  // Sort handling
  const handleSort = (field) => {
    if (sortBy === field) {
      // Toggle order if same field
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };
  
  const getSortIcon = (field) => {
    if (sortBy !== field) return <ArrowUpDown className="h-4 w-4 opacity-50" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />;
  };

  // Delete entry handlers
  const handleDeleteClick = (entry) => {
    setEntryToDelete(entry);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!entryToDelete) return;
    
    try {
      setDeleting(true);
      await pettyCashAPI.deleteEntry(entryToDelete.id);
      toast.success('Entry deleted successfully');
      setDeleteDialogOpen(false);
      setEntryToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete entry');
    } finally {
      setDeleting(false);
    }
  };

  // Fetch deleted entries
  const fetchDeletedEntries = async () => {
    try {
      setLoadingDeleted(true);
      const response = await pettyCashAPI.getDeletedEntries();
      setDeletedEntries(response.data);
    } catch (error) {
      toast.error('Failed to load deleted entries');
    } finally {
      setLoadingDeleted(false);
    }
  };

  // Restore entry handler
  const handleRestoreEntry = async (entryId) => {
    try {
      await pettyCashAPI.restoreEntry(entryId);
      toast.success('Entry restored successfully');
      fetchDeletedEntries();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore entry');
    }
  };

  // Fetch analytics data for reports
  const fetchAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      const params = {};
      if (reportDateRange.start_date) params.start_date = reportDateRange.start_date;
      if (reportDateRange.end_date) params.end_date = reportDateRange.end_date;
      
      const response = await pettyCashAPI.getAnalytics(params);
      setAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
      toast.error('Failed to load analytics data');
    } finally {
      setAnalyticsLoading(false);
    }
  };

  // Handle bill file upload (for new expenses)
  const handleBillUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    try {
      setUploading(true);
      const uploadedUrls = [...expenseForm.bill_images];
      
      // Upload each file
      for (let i = 0; i < files.length; i++) {
        const response = await pettyCashAPI.uploadBill(await compressImageFile(files[i]));
        uploadedUrls.push(response.data.url);
      }
      
      setExpenseForm({ ...expenseForm, bill_images: uploadedUrls });
      toast.success(`${files.length} bill(s) uploaded successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload bill');
    } finally {
      setUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Remove a bill from the list
  const handleRemoveBill = (index) => {
    const newBillImages = expenseForm.bill_images.filter((_, i) => i !== index);
    setExpenseForm({ ...expenseForm, bill_images: newBillImages });
  };
  
  // Handle bill upload for edit expense form
  const handleEditBillUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    try {
      setUploading(true);
      const uploadedUrls = [...editExpenseForm.bill_images];
      
      for (let i = 0; i < files.length; i++) {
        const response = await pettyCashAPI.uploadBill(await compressImageFile(files[i]));
        uploadedUrls.push(response.data.url);
      }
      
      setEditExpenseForm({ ...editExpenseForm, bill_images: uploadedUrls });
      toast.success(`${files.length} bill(s) uploaded successfully`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload bill');
    } finally {
      setUploading(false);
      // Reset file input
      if (editBillInputRef.current) {
        editBillInputRef.current.value = '';
      }
    }
  };
  
  // Remove a bill from edit form
  const handleRemoveEditBill = (index) => {
    const newBillImages = editExpenseForm.bill_images.filter((_, i) => i !== index);
    setEditExpenseForm({ ...editExpenseForm, bill_images: newBillImages });
  };

  // Handle bill upload to existing expense
  const handleExistingExpenseBillUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingExpenseId) return;
    
    try {
      setUploading(true);
      await pettyCashAPI.uploadExpenseBill(uploadingExpenseId, await compressImageFile(file));
      toast.success('Bill uploaded successfully');
      // Refresh data
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload bill');
    } finally {
      setUploading(false);
      setUploadingExpenseId(null);
      if (existingBillInputRef.current) {
        existingBillInputRef.current.value = '';
      }
    }
  };

  const triggerExistingExpenseBillUpload = (expenseId) => {
    setUploadingExpenseId(expenseId);
    existingBillInputRef.current?.click();
  };

  // Download PDF expense report
  const handleDownloadReport = async () => {
    try {
      setDownloadingPdf(true);
      const params = {};
      if (reportFilters.start_date) params.start_date = reportFilters.start_date;
      if (reportFilters.end_date) params.end_date = reportFilters.end_date;
      if (reportFilters.user_id) params.user_id = reportFilters.user_id;
      if (reportFilters.client_id) params.client_id = reportFilters.client_id;
      if (reportFilters.category) params.category = reportFilters.category;
      if (reportFilters.status) params.status = reportFilters.status;
      
      const response = await pettyCashAPI.downloadExpenseReport(params);
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `expense_report_${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded successfully');
      setReportDialogOpen(false);
    } catch (error) {
      toast.error('Failed to download report');
      console.error(error);
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handleCreateTransfer = async () => {
    if (!transferForm.recipient_user_id || !transferForm.amount) {
      toast.error('Please select recipient and enter amount');
      return;
    }
    
    const transferAmount = parseFloat(transferForm.amount);
    
    // Validate sufficient balance (for non-admin users)
    if (!isAdmin && myBalance?.available_balance < transferAmount) {
      toast.error(`Insufficient balance. You have ${formatCurrency(myBalance?.available_balance || 0)} available.`);
      return;
    }
    
    try {
      setSaving(true);
      await pettyCashAPI.createTransfer({
        recipient_user_id: transferForm.recipient_user_id,
        amount: transferAmount,
        notes: transferForm.notes,
        transfer_date: transferForm.transfer_date
      });
      toast.success('Petty cash transferred successfully');
      setTransferDialogOpen(false);
      setTransferForm({ recipient_user_id: '', amount: '', notes: '', transfer_date: new Date().toISOString().split('T')[0] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to transfer');
    } finally {
      setSaving(false);
    }
  };

  const handleBulkUpload = async () => {
    if (!bulkRecipient) { toast.error('Select a recipient'); return; }
    if (!bulkFile) { toast.error('Select an Excel file'); return; }
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      fd.append('recipient_user_id', bulkRecipient);
      fd.append('notes', bulkNotes || 'Bulk transfer upload');
      const { data } = await pettyCashAPI.bulkUploadTransfers(fd);
      setBulkResult(data);
      if (data.created_count > 0) {
        toast.success(`${data.created_count} transfers created`);
        fetchData();
      }
      if (data.error_count > 0) {
        toast.error(`${data.error_count} rows had errors`);
      }
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Upload failed';
      toast.error(msg);
      console.error('Bulk upload error:', error.response?.data || error.message);
    } finally {
      setBulkUploading(false);
    }
  };

  const handleBulkUndo = async () => {
    if (!bulkResult?.batch_id) return;
    try {
      const { data } = await pettyCashAPI.undoBatch(bulkResult.batch_id);
      toast.success(`Undo complete: ${data.deleted_count} transfers removed`);
      setBulkResult(null);
      setBulkFile(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Undo failed');
    }
  };


  const handleViewMemberTransfers = async (userId) => {
    setMemberTransfersLoading(true);
    setMemberTransfersOpen(true);
    setMemberTransfers(null);
    try {
      const { data } = await pettyCashAPI.getUserTransfers(userId);
      setMemberTransfers(data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load transfers');
    } finally {
      setMemberTransfersLoading(false);
    }
  };

  const handleDeleteTransfer = async (transferId) => {
    try {
      await pettyCashAPI.deleteEntry(transferId);
      toast.success('Transfer deleted');
      setDeleteTransferId(null);
      // Refresh the list
      if (memberTransfers) {
        handleViewMemberTransfers(memberTransfers.user_id);
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete');
    }
  };

  const toggleTransferSelection = (id) => {
    setSelectedTransferIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (!memberTransfers?.transfers) return;
    const allIds = memberTransfers.transfers.map(t => t.id);
    if (selectedTransferIds.size === allIds.length) {
      setSelectedTransferIds(new Set());
    } else {
      setSelectedTransferIds(new Set(allIds));
    }
  };

  const handleBulkDeleteTransfers = async () => {
    if (selectedTransferIds.size === 0) return;
    setBulkDeleting(true);
    try {
      const { data } = await pettyCashAPI.bulkDeleteTransfers([...selectedTransferIds]);
      toast.success(`${data.deleted_count} transfers deleted`);
      setSelectedTransferIds(new Set());
      if (memberTransfers) handleViewMemberTransfers(memberTransfers.user_id);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkDeleting(false);
    }
  };




  const handleCreateExpense = async () => {
    if (!expenseForm.amount || !expenseForm.description || !expenseForm.category) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      setSaving(true);
      await pettyCashAPI.createExpense({
        amount: parseFloat(expenseForm.amount),
        description: expenseForm.description,
        expense_date: expenseForm.expense_date,
        property_id: expenseForm.property_id || null,
        vendor_id: expenseForm.vendor_id || null,
        po_id: expenseForm.po_id || null,
        client_id: (!expenseForm.po_id && expenseForm.client_id) ? expenseForm.client_id : null,
        category: expenseForm.category,
        other_category: expenseForm.other_category || null,
        bill_number: expenseForm.bill_number || null,
        bill_images: expenseForm.bill_images.length > 0 ? expenseForm.bill_images : null,
        purchased_by: expenseForm.purchased_by || null,
        notes: expenseForm.notes || null
      });
      toast.success('Expense recorded and pending approval');
      setExpenseDialogOpen(false);
      setExpenseForm({
        amount: '',
        description: '',
        expense_date: new Date().toISOString().split('T')[0],
        property_id: '',
        vendor_id: '',
        po_id: '',
        client_id: '',
        category: '',
        other_category: '',
        bill_number: '',
        bill_images: [],
        purchased_by: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record expense');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateReturn = async () => {
    if (!returnForm.amount) {
      toast.error('Please enter amount to return');
      return;
    }
    
    try {
      setSaving(true);
      await pettyCashAPI.createReturn({
        amount: parseFloat(returnForm.amount),
        notes: returnForm.notes
      });
      toast.success('Petty cash returned successfully');
      setReturnDialogOpen(false);
      setReturnForm({ amount: '', notes: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return');
    } finally {
      setSaving(false);
    }
  };

  // Edit expense handlers
  const handleOpenEditExpense = (expense) => {
    setEditingExpense(expense);
    setEditExpenseForm({
      amount: expense.amount?.toString() || '',
      description: expense.description || '',
      expense_date: expense.expense_date?.split('T')[0] || '',
      property_id: expense.property_id || '',
      vendor_id: expense.vendor_id || '',
      po_id: expense.po_id || '',
      client_id: expense.client_id || '',
      category: expense.category || '',
      other_category: expense.other_category || '',
      bill_number: expense.bill_number || '',
      bill_images: expense.bill_images || (expense.bill_image_url ? [expense.bill_image_url] : []),
      purchased_by: expense.purchased_by || '',
      notes: expense.notes || ''
    });
    setEditExpenseDialogOpen(true);
  };

  // Edit Transfer state and handlers
  const [editTransferDialogOpen, setEditTransferDialogOpen] = useState(false);
  const [editTransferForm, setEditTransferForm] = useState({
    recipient_user_id: '',
    amount: '',
    notes: ''
  });

  const handleOpenEditTransfer = (transfer) => {
    setEditingTransfer(transfer);
    setEditTransferForm({
      recipient_user_id: transfer.to_user_id || '',
      amount: transfer.amount?.toString() || '',
      notes: transfer.notes || ''
    });
    setEditTransferDialogOpen(true);
  };

  const handleUpdateTransfer = async () => {
    if (!editTransferForm.amount || !editTransferForm.recipient_user_id) {
      toast.error('Please fill in recipient and amount');
      return;
    }

    try {
      setSaving(true);
      await pettyCashAPI.updateTransfer(editingTransfer.id, {
        recipient_user_id: editTransferForm.recipient_user_id,
        amount: parseFloat(editTransferForm.amount),
        notes: editTransferForm.notes || null
      });
      toast.success('Transfer updated successfully');
      setEditTransferDialogOpen(false);
      setEditingTransfer(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update transfer');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateExpense = async () => {
    if (!editExpenseForm.amount || !editExpenseForm.description || !editExpenseForm.category) {
      toast.error('Please fill in amount, description, and category');
      return;
    }

    try {
      setSaving(true);
      await pettyCashAPI.updateExpense(editingExpense.id, {
        amount: parseFloat(editExpenseForm.amount),
        description: editExpenseForm.description,
        expense_date: editExpenseForm.expense_date,
        property_id: editExpenseForm.property_id || null,
        vendor_id: editExpenseForm.vendor_id || null,
        po_id: editExpenseForm.po_id || null,
        client_id: (!editExpenseForm.po_id && editExpenseForm.client_id) ? editExpenseForm.client_id : null,
        category: editExpenseForm.category,
        other_category: editExpenseForm.other_category || null,
        bill_number: editExpenseForm.bill_number || null,
        bill_images: editExpenseForm.bill_images.length > 0 ? editExpenseForm.bill_images : null,
        purchased_by: editExpenseForm.purchased_by || null,
        notes: editExpenseForm.notes || null
      });
      toast.success('Expense updated successfully');
      setEditExpenseDialogOpen(false);
      setEditingExpense(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update expense');
    } finally {
      setSaving(false);
    }
  };

  const handleApproveExpense = async (expense) => {
    try {
      setSaving(true);
      await pettyCashAPI.updateExpense(expense.id, { status: 'approved' });
      toast.success('Expense approved');
      setApprovalDialogOpen(false);
      setSelectedExpense(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setSaving(false);
    }
  };

  const handleRejectExpense = async (expense) => {
    if (!rejectionReason) {
      toast.error('Please provide a rejection reason');
      return;
    }
    
    try {
      setSaving(true);
      await pettyCashAPI.updateExpense(expense.id, { 
        status: 'rejected',
        rejection_reason: rejectionReason
      });
      toast.success('Expense rejected');
      setApprovalDialogOpen(false);
      setSelectedExpense(null);
      setRejectionReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setSaving(false);
    }
  };

  const handleRevertToPending = async (expense) => {
    try {
      setSaving(true);
      await pettyCashAPI.updateExpense(expense.id, { status: 'pending' });
      toast.success('Moved back to Pending');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  // Bulk approval handlers
  const handleSelectAllApprovals = (checked) => {
    if (checked) {
      setSelectedApprovals(displayedApprovals.map(e => e.id));
    } else {
      setSelectedApprovals([]);
    }
  };

  // Unique team members present in pending approvals (for the filter dropdown)
  const approvalMembers = Array.from(
    new Map(pendingApprovals.map(e => [e.user_id || e.user_name, e.user_name])).entries()
  ).map(([id, name]) => ({ id, name })).sort((a, b) => (a.name || '').localeCompare(b.name || ''));

  const toggleApprovalSort = (key) => {
    setApprovalSort(prev => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' });
  };

  // Filtered + sorted approvals for display
  const displayedApprovals = (() => {
    let list = [...pendingApprovals];
    if (approvalMemberFilter !== 'all') {
      list = list.filter(e => (e.user_id || e.user_name) === approvalMemberFilter);
    }
    const { key, dir } = approvalSort;
    const getVal = (e) => {
      switch (key) {
        case 'date': return e.expense_date || '';
        case 'member': return (e.user_name || '').toLowerCase();
        case 'description': return (e.description || '').toLowerCase();
        case 'category': return (e.category || '').toLowerCase();
        case 'amount': return e.amount || 0;
        default: return '';
      }
    };
    list.sort((a, b) => {
      const va = getVal(a), vb = getVal(b);
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  })();

  const handleSelectApproval = (expenseId, checked) => {
    if (checked) {
      setSelectedApprovals([...selectedApprovals, expenseId]);
    } else {
      setSelectedApprovals(selectedApprovals.filter(id => id !== expenseId));
    }
  };

  const handleBulkApprove = async () => {
    if (selectedApprovals.length === 0) return;
    
    try {
      setBulkApproving(true);
      // Approve each selected expense
      for (const expenseId of selectedApprovals) {
        await pettyCashAPI.updateExpense(expenseId, { status: 'approved' });
      }
      toast.success(`${selectedApprovals.length} expense(s) approved`);
      setSelectedApprovals([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve expenses');
    } finally {
      setBulkApproving(false);
    }
  };
  
  // Get all bill URLs for an expense (handles both old bill_image_url and new bill_images)
  const getExpenseBills = (expense) => {
    const bills = [];
    if (expense.bill_images && expense.bill_images.length > 0) {
      bills.push(...expense.bill_images);
    } else if (expense.bill_image_url) {
      bills.push(expense.bill_image_url);
    }
    return bills;
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      case 'pending':
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'transfer': return <Send className="h-4 w-4 text-blue-600" />;
      case 'expense': return <Receipt className="h-4 w-4 text-red-600" />;
      case 'return': return <RotateCcw className="h-4 w-4 text-green-600" />;
      default: return <IndianRupee className="h-4 w-4" />;
    }
  };

  if (firstLoad) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Petty Cash</h1>
          <p className="text-muted-foreground mt-1">Manage petty cash transfers and expenses</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => setTransferDialogOpen(true)} data-testid="transfer-btn">
            <Send className="h-4 w-4 mr-2" />
            Transfer Cash
          </Button>
          {isAdmin && (
            <Button variant="outline" onClick={() => { setBulkUploadOpen(true); setBulkResult(null); setBulkFile(null); setBulkRecipient(''); setBulkNotes(''); }} data-testid="bulk-upload-btn">
              <Upload className="h-4 w-4 mr-2" />
              Upload Excel
            </Button>
          )}
          <Button variant="outline" onClick={() => setExpenseDialogOpen(true)} data-testid="expense-btn">
            <Receipt className="h-4 w-4 mr-2" />
            Record Expense
          </Button>
          <Button variant="outline" onClick={() => setReturnDialogOpen(true)} data-testid="return-btn">
            <RotateCcw className="h-4 w-4 mr-2" />
            Return Cash
          </Button>
          {isAdmin && (
            <Button variant="secondary" onClick={() => setReportDialogOpen(true)} data-testid="export-report-btn">
              <Download className="h-4 w-4 mr-2" />
              Export Report
            </Button>
          )}
        </div>
      </div>

      {/* Rejected submissions alert (for the logged-in user) */}
      {(() => {
        const myRejected = transactions.filter(t => t.type === 'expense' && t.status === 'rejected' && t.user_id === user?.id);
        if (myRejected.length === 0 || rejectedBannerDismissed) return null;
        return (
          <div className="rounded-lg border border-red-300 bg-red-50 p-4" data-testid="rejected-alert-banner">
            <div className="flex items-start gap-3">
              <XCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-red-800">
                  {myRejected.length} of your petty cash {myRejected.length === 1 ? 'submission was' : 'submissions were'} rejected
                </p>
                <ul className="mt-2 space-y-1.5">
                  {myRejected.slice(0, 5).map(t => (
                    <li key={t.id} className="text-sm text-red-700" data-testid={`rejected-alert-item-${t.id}`}>
                      <span className="font-medium">{t.description || 'Expense'}</span>
                      <span className="text-red-600"> · {formatCurrency(t.amount)}</span>
                      {t.rejection_reason && <span className="block text-red-600 italic">Reason: {t.rejection_reason}</span>}
                    </li>
                  ))}
                </ul>
                {myRejected.length > 5 && (
                  <p className="text-xs text-red-600 mt-1">+{myRejected.length - 5} more — see them in My Transactions below.</p>
                )}
              </div>
              <button onClick={() => setRejectedBannerDismissed(true)} className="text-red-500 hover:text-red-700" data-testid="dismiss-rejected-alert">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        );
      })()}

      <Card className="border-l-4 border-l-primary" data-testid="my-balance-card">
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2">
            <Wallet className="h-5 w-5" />
            My Petty Cash Balance
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="p-3 rounded-lg bg-blue-50">
              <p className="text-xs text-muted-foreground">Received</p>
              <p className="text-xl font-bold text-blue-700">{formatCurrency(myBalance?.total_received || 0)}</p>
            </div>
            <div className="p-3 rounded-lg bg-indigo-50">
              <p className="text-xs text-muted-foreground">Transferred Out</p>
              <p className="text-xl font-bold text-indigo-700">{formatCurrency(myBalance?.total_sent || 0)}</p>
            </div>
            <div className="p-3 rounded-lg bg-green-50">
              <p className="text-xs text-muted-foreground">Approved Spent</p>
              <p className="text-xl font-bold text-green-700">{formatCurrency(myBalance?.total_approved_spent || 0)}</p>
            </div>
            <div className="p-3 rounded-lg bg-amber-50">
              <p className="text-xs text-muted-foreground">Pending Approval</p>
              <p className="text-xl font-bold text-amber-700">{formatCurrency(myBalance?.total_pending_spent || 0)}</p>
            </div>
            <div className="p-3 rounded-lg bg-purple-50">
              <p className="text-xs text-muted-foreground">Returned</p>
              <p className="text-xl font-bold text-purple-700">{formatCurrency(myBalance?.total_returned || 0)}</p>
            </div>
            <div className="p-3 rounded-lg bg-primary/10">
              <p className="text-xs text-muted-foreground">Available Balance</p>
              <p className="text-xl font-bold text-primary">{formatCurrency(myBalance?.available_balance || 0)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Warning: Expenses Without Bills */}
      {isAdmin && expensesWithoutBills?.count > 0 && (
        <Card className="border-l-4 border-l-red-500 bg-red-50" data-testid="no-bills-warning">
          <CardContent className="py-4">
            <div className="flex items-center justify-between cursor-pointer" onClick={() => setShowNoBillsExpanded(!showNoBillsExpanded)}>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-red-100">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="font-semibold text-red-700">
                    {expensesWithoutBills.count} Expense{expensesWithoutBills.count > 1 ? 's' : ''} Without Bills
                  </p>
                  <p className="text-sm text-red-600">
                    Click to {showNoBillsExpanded ? 'collapse' : 'expand'} and upload missing bills
                  </p>
                </div>
              </div>
              <Badge variant="destructive" className="text-lg px-3 py-1">
                {expensesWithoutBills.count}
              </Badge>
            </div>
            
            {/* Hidden file input for existing expense bill upload */}
            <input
              type="file"
              ref={existingBillInputRef}
              className="hidden"
              accept="image/*,application/pdf,.pdf,.doc,.docx,.xls,.xlsx,.csv"
              onChange={handleExistingExpenseBillUpload}
            />
            
            {/* Expandable list of expenses */}
            {showNoBillsExpanded && (
              <div className="mt-4 space-y-2">
                {expensesWithoutBills.expenses?.map((expense) => (
                  <div key={expense.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-red-200">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">{expense.description}</p>
                        <Badge variant="outline" className="text-xs">{expense.category}</Badge>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                        <span className="font-semibold text-red-700">{formatCurrency(expense.amount)}</span>
                        <span>•</span>
                        <span>{expense.user_name}</span>
                        <span>•</span>
                        <span>{formatDate(expense.expense_date)}</span>
                        {expense.bill_number && (
                          <>
                            <span>•</span>
                            <span>Bill #: {expense.bill_number}</span>
                          </>
                        )}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-300 text-red-700 hover:bg-red-100"
                      onClick={() => triggerExistingExpenseBillUpload(expense.id)}
                      disabled={uploading && uploadingExpenseId === expense.id}
                      data-testid={`upload-bill-${expense.id}`}
                    >
                      {uploading && uploadingExpenseId === expense.id ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                      ) : (
                        <><Upload className="h-4 w-4 mr-2" />Upload Bill</>
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Admin Dashboard */}
      {isAdmin && dashboard && (
        <Card data-testid="admin-dashboard">
          <CardHeader>
            <CardTitle>Petty Cash Overview</CardTitle>
            <CardDescription>System-wide petty cash summary</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
                <p className="text-sm text-muted-foreground">Total Transferred</p>
                <p className="text-2xl font-bold text-blue-700">{formatCurrency(dashboard.summary?.total_transferred || 0)}</p>
              </div>
              <div className="p-4 rounded-lg bg-green-50 border border-green-100">
                <p className="text-sm text-muted-foreground">Total Approved</p>
                <p className="text-2xl font-bold text-green-700">{formatCurrency(dashboard.summary?.total_approved_expenses || 0)}</p>
              </div>
              <div className="p-4 rounded-lg bg-amber-50 border border-amber-100">
                <p className="text-sm text-muted-foreground">Pending Approval</p>
                <div className="text-2xl font-bold text-amber-700 flex items-center">
                  {formatCurrency(dashboard.summary?.total_pending_expenses || 0)}
                  {dashboard.summary?.pending_approvals_count > 0 && (
                    <Badge className="ml-2 bg-amber-600">{dashboard.summary.pending_approvals_count}</Badge>
                  )}
                </div>
              </div>
              <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-sm text-muted-foreground">Net Outstanding</p>
                <p className="text-2xl font-bold text-primary">{formatCurrency(dashboard.summary?.net_outstanding || 0)}</p>
              </div>
            </div>

            {/* User Balances */}
            {dashboard.user_balances?.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium">Team Member Balances</h4>
                  <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-balances-btn">
                    <RotateCw className="h-4 w-4 mr-1" />Refresh
                  </Button>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Team Member</TableHead>
                      <TableHead className="text-right">Received</TableHead>
                      <TableHead className="text-right">Spent</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                      <TableHead className="text-right">Returned</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dashboard.user_balances.map(ub => (
                      <TableRow key={ub.user_id} className="cursor-pointer hover:bg-slate-50" onClick={() => handleViewMemberTransfers(ub.user_id)} data-testid={`member-row-${ub.user_id}`}>
                        <TableCell className="font-medium">{ub.user_name}</TableCell>
                        <TableCell className="text-right text-blue-600">{formatCurrency(ub.received)}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(ub.spent)}</TableCell>
                        <TableCell className="text-right text-amber-600">{formatCurrency(ub.pending)}</TableCell>
                        <TableCell className="text-right text-purple-600">{formatCurrency(ub.returned)}</TableCell>
                        <TableCell className="text-right font-bold">{formatCurrency(ub.balance)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tabs for Transactions and Approvals */}
      <Tabs value={activeTab} onValueChange={(value) => {
        setActiveTab(value);
        if (value === 'reports' && !analytics) {
          fetchAnalytics();
        }
      }}>
        <TabsList>
          <TabsTrigger value="transactions">My Transactions</TabsTrigger>
          {isAdmin && (
            <TabsTrigger value="approvals" className="relative">
              Approvals
              {(dashboard?.summary?.pending_approvals_count || 0) > 0 && (
                <Badge className="ml-2 bg-amber-600">{dashboard.summary.pending_approvals_count}</Badge>
              )}
            </TabsTrigger>
          )}
          {isAdmin && (
            <TabsTrigger value="reports">
              <BarChart3 className="h-4 w-4 mr-1" />
              Reports
            </TabsTrigger>
          )}
        </TabsList>

        <TabsContent value="transactions">
          <Card>
            <CardHeader className="pb-3">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-muted-foreground">Sort by:</span>
                  <Select value={sortBy} onValueChange={(value) => { setSortBy(value); }}>
                    <SelectTrigger className="w-[140px] h-8" data-testid="sort-by-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recorded_at">Recently Added</SelectItem>
                      <SelectItem value="created_at">Transfer/Entry Date</SelectItem>
                      <SelectItem value="expense_date">Expense Date</SelectItem>
                      <SelectItem value="amount">Amount</SelectItem>
                      <SelectItem value="category">Category</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                    data-testid="sort-order-btn"
                  >
                    {sortOrder === 'asc' ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                    {sortOrder === 'asc' ? 'Ascending' : 'Descending'}
                  </Button>
                  <span className="text-sm text-muted-foreground ml-2">Filter by Member:</span>
                  <Select value={filterMember} onValueChange={setFilterMember}>
                    <SelectTrigger className="w-[160px] h-8" data-testid="filter-member-select">
                      <SelectValue placeholder="All Members" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Members</SelectItem>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {isAdmin && (
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => { setShowDeletedEntries(!showDeletedEntries); if (!showDeletedEntries) fetchDeletedEntries(); }}
                    data-testid="show-deleted-btn"
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    {showDeletedEntries ? 'Hide Deleted' : 'View Deleted'}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="pt-0">
              {/* Deleted Entries Section */}
              {isAdmin && showDeletedEntries && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-red-700 flex items-center gap-2">
                      <Trash2 className="h-4 w-4" />
                      Deleted Entries ({deletedEntries.length})
                    </h4>
                    <Button variant="ghost" size="sm" onClick={fetchDeletedEntries} disabled={loadingDeleted}>
                      <RotateCw className={`h-4 w-4 ${loadingDeleted ? 'animate-spin' : ''}`} />
                    </Button>
                  </div>
                  {loadingDeleted ? (
                    <div className="flex justify-center py-4">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : deletedEntries.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-4">No deleted entries</p>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Type</TableHead>
                          <TableHead>Description</TableHead>
                          <TableHead className="text-right">Amount</TableHead>
                          <TableHead>Deleted By</TableHead>
                          <TableHead>Deleted At</TableHead>
                          <TableHead>Action</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {deletedEntries.map(entry => (
                          <TableRow key={entry.id} className="bg-red-50/50">
                            <TableCell className="capitalize">{entry.type}</TableCell>
                            <TableCell>{entry.description || entry.notes || '-'}</TableCell>
                            <TableCell className="text-right font-medium">{formatCurrency(entry.amount)}</TableCell>
                            <TableCell>{entry.deleted_by_name || '-'}</TableCell>
                            <TableCell>{entry.deleted_at ? formatDate(entry.deleted_at) : '-'}</TableCell>
                            <TableCell>
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-green-600 border-green-300 hover:bg-green-50"
                                onClick={() => handleRestoreEntry(entry.id)}
                                data-testid={`restore-entry-${entry.id}`}
                              >
                                <RotateCcw className="h-4 w-4 mr-1" />
                                Restore
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </div>
              )}
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Type</TableHead>
                    <TableHead 
                      className="cursor-pointer hover:bg-muted/50" 
                      onClick={() => handleSort('created_at')}
                    >
                      <div className="flex items-center gap-1">
                        Date {getSortIcon('created_at')}
                      </div>
                    </TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>PO Link</TableHead>
                    <TableHead 
                      className="text-right cursor-pointer hover:bg-muted/50" 
                      onClick={() => handleSort('amount')}
                    >
                      <div className="flex items-center justify-end gap-1">
                        Amount {getSortIcon('amount')}
                      </div>
                    </TableHead>
                    <TableHead>Status</TableHead>
                    {isAdmin && <TableHead>Actions</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={isAdmin ? 7 : 6} className="text-center text-muted-foreground py-8">
                        No transactions found
                      </TableCell>
                    </TableRow>
                  ) : (
                    transactions.map(tx => {
                      // Check if expense without bill
                      const isExpenseWithoutBill = tx.type === 'expense' && tx.status === 'approved' && !tx.has_bill && !tx.bill_image_url && !tx.bill_number;
                      return (
                        <TableRow 
                          key={tx.id}
                          className={isExpenseWithoutBill ? 'bg-red-50 hover:bg-red-100' : ''}
                        >
                          <TableCell>
                            <div className="flex items-center gap-2">
                              {getTransactionIcon(tx.type)}
                              <span className="capitalize">{tx.type}</span>
                              {isExpenseWithoutBill && (
                                <AlertTriangle className="h-4 w-4 text-red-600" title="No bill attached" />
                              )}
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(tx.created_at)}</TableCell>
                          <TableCell>
                            {tx.type === 'transfer' && (
                              <span>Transfer to {tx.to_user_name}</span>
                            )}
                            {tx.type === 'expense' && (
                              <div>
                                <span>{tx.description} ({tx.category})</span>
                                {tx.purchased_by && (
                                  <p className="text-xs text-muted-foreground">By: {tx.purchased_by}</p>
                                )}
                              </div>
                            )}
                            {tx.type === 'return' && (
                              <span>Cash returned</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {(tx.po_number || tx.client_name || tx.vendor_name) ? (
                              <div className="flex flex-col gap-1 items-start">
                                {tx.po_number && (
                                  <Badge variant="outline" className="gap-1 font-mono">
                                    <LinkIcon className="h-3 w-3" />
                                    {tx.po_number}
                                  </Badge>
                                )}
                                {tx.client_name && (
                                  <span className="text-xs flex items-center gap-1 text-muted-foreground">
                                    <Users className="h-3 w-3" />
                                    {tx.client_name}
                                  </span>
                                )}
                                {!tx.po_number && !tx.client_name && tx.vendor_name && (
                                  <span className="text-sm">{tx.vendor_name}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className={`text-right font-medium ${tx.type === 'transfer' ? 'text-blue-600' : tx.type === 'expense' ? 'text-red-600' : 'text-green-600'}`}>
                            {tx.type === 'expense' ? '-' : ''}{formatCurrency(tx.amount)}
                          </TableCell>
                          <TableCell>
                            {tx.type === 'expense' ? (
                              <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1">
                                {getStatusBadge(tx.status)}
                                {/* Show bill icons - support both single and multiple bills */}
                                {getExpenseBills(tx).length > 0 && (
                                  <div className="flex items-center gap-0.5">
                                    {getExpenseBills(tx).map((billUrl, idx) => (
                                      <div key={idx} className="flex items-center">
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 w-6 p-0"
                                          onClick={() => viewBill(billUrl)}
                                          title={`View Bill ${idx + 1}`}
                                        >
                                          <Eye className="h-3 w-3" />
                                        </Button>
                                        <Button 
                                          size="sm" 
                                          variant="ghost" 
                                          className="h-6 w-6 p-0"
                                          onClick={() => downloadBill(billUrl, `bill_${tx.id}_${idx + 1}`)}
                                          title={`Download Bill ${idx + 1}`}
                                        >
                                          <Download className="h-3 w-3" />
                                        </Button>
                                      </div>
                                    ))}
                                    {getExpenseBills(tx).length > 1 && (
                                      <span className="text-xs text-muted-foreground">({getExpenseBills(tx).length})</span>
                                    )}
                                  </div>
                                )}
                              </div>
                              {tx.status === 'rejected' && tx.rejection_reason && (
                                <span className="text-xs text-red-600 italic" data-testid={`tx-rejection-reason-${tx.id}`}>
                                  Reason: {tx.rejection_reason}
                                </span>
                              )}
                              </div>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {tx.type === 'expense' && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => handleOpenEditExpense(tx)}
                                    title="Edit Expense"
                                    data-testid={`edit-expense-${tx.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                )}
                                {tx.type === 'transfer' && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => handleOpenEditTransfer(tx)}
                                    title="Edit Transfer"
                                    data-testid={`edit-transfer-${tx.id}`}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                )}
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  onClick={() => handleDeleteClick(tx)}
                                  title="Delete Entry"
                                  data-testid={`delete-entry-${tx.id}`}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {isAdmin && (
          <TabsContent value="approvals">
            <Card>
              <CardContent className="pt-6">
                {/* Status + member filters */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <span className="text-sm font-medium text-muted-foreground">Status:</span>
                  <Select value={approvalStatusFilter} onValueChange={setApprovalStatusFilter}>
                    <SelectTrigger className="w-[160px]" data-testid="approval-status-filter">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                      <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm font-medium text-muted-foreground ml-2">Member:</span>
                  <Select value={approvalMemberFilter} onValueChange={setApprovalMemberFilter}>
                    <SelectTrigger className="w-[220px]" data-testid="approval-member-filter">
                      <SelectValue placeholder="All Members" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Members</SelectItem>
                      {approvalMembers.map(m => (
                        <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Badge variant="secondary" data-testid="approval-filter-count">{displayedApprovals.length} shown</Badge>
                </div>
                {/* Bulk Action Bar */}
                {selectedApprovals.length > 0 && (
                  <div className="flex items-center justify-between p-3 mb-4 bg-primary/10 border border-primary/20 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary">{selectedApprovals.length} selected</Badge>
                      <span className="text-sm text-muted-foreground">expense(s)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setSelectedApprovals([])}
                      >
                        Clear Selection
                      </Button>
                      <Button 
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={handleBulkApprove}
                        disabled={bulkApproving}
                        data-testid="bulk-approve-btn"
                      >
                        {bulkApproving ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <CheckCircle className="h-4 w-4 mr-2" />
                        )}
                        Approve Selected
                      </Button>
                    </div>
                  </div>
                )}
                
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[40px]">
                        <Checkbox
                          checked={selectedApprovals.length === displayedApprovals.length && displayedApprovals.length > 0}
                          onCheckedChange={handleSelectAllApprovals}
                          data-testid="select-all-approvals"
                        />
                      </TableHead>
                      <TableHead>
                        <button className="flex items-center gap-1 hover:text-foreground" onClick={() => toggleApprovalSort('date')} data-testid="sort-date">
                          Date {approvalSort.key === 'date' ? (approvalSort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />}
                        </button>
                      </TableHead>
                      <TableHead>
                        <button className="flex items-center gap-1 hover:text-foreground" onClick={() => toggleApprovalSort('member')} data-testid="sort-member">
                          Team Member {approvalSort.key === 'member' ? (approvalSort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />}
                        </button>
                      </TableHead>
                      <TableHead>
                        <button className="flex items-center gap-1 hover:text-foreground" onClick={() => toggleApprovalSort('description')} data-testid="sort-description">
                          Description {approvalSort.key === 'description' ? (approvalSort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />}
                        </button>
                      </TableHead>
                      <TableHead>
                        <button className="flex items-center gap-1 hover:text-foreground" onClick={() => toggleApprovalSort('category')} data-testid="sort-category">
                          Category {approvalSort.key === 'category' ? (approvalSort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />}
                        </button>
                      </TableHead>
                      <TableHead>PO Link</TableHead>
                      <TableHead className="text-right">
                        <button className="flex items-center gap-1 hover:text-foreground ml-auto" onClick={() => toggleApprovalSort('amount')} data-testid="sort-amount">
                          Amount {approvalSort.key === 'amount' ? (approvalSort.dir === 'asc' ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />) : <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />}
                        </button>
                      </TableHead>
                      <TableHead>Bill</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {displayedApprovals.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                          No {approvalStatusFilter === 'all' ? '' : approvalStatusFilter} expenses
                        </TableCell>
                      </TableRow>
                    ) : (
                      displayedApprovals.map(expense => {
                        const expenseBills = getExpenseBills(expense);
                        const hasBill = expenseBills.length > 0 || expense.bill_number;
                        return (
                          <TableRow 
                            key={expense.id} 
                            className={`${!hasBill ? 'bg-red-50' : ''} ${selectedApprovals.includes(expense.id) ? 'bg-primary/5' : ''}`}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedApprovals.includes(expense.id)}
                                onCheckedChange={(checked) => handleSelectApproval(expense.id, checked)}
                                data-testid={`select-approval-${expense.id}`}
                              />
                            </TableCell>
                            <TableCell>{formatDate(expense.expense_date)}</TableCell>
                            <TableCell>
                              <div>
                                <span className="font-medium">{expense.user_name}</span>
                                {expense.purchased_by && (
                                  <p className="text-xs text-muted-foreground">Purchased by: {expense.purchased_by}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div>
                                <span>{expense.description}</span>
                                {expense.vendor_name && (
                                  <p className="text-xs text-muted-foreground">Vendor: {expense.vendor_name}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell><Badge variant="outline">{expense.category}</Badge></TableCell>
                            <TableCell>
                              {(expense.po_number || expense.client_name) ? (
                                <div className="flex flex-col gap-1 items-start">
                                  {expense.po_number && (
                                    <Badge variant="secondary" className="gap-1 font-mono">
                                      <LinkIcon className="h-3 w-3" />
                                      {expense.po_number}
                                    </Badge>
                                  )}
                                  {expense.client_name && (
                                    <span className="text-xs flex items-center gap-1 text-muted-foreground">
                                      <Users className="h-3 w-3" />
                                      {expense.client_name}
                                    </span>
                                  )}
                                </div>
                              ) : (
                                <span className="text-muted-foreground text-xs">Not linked</span>
                              )}
                            </TableCell>
                            <TableCell className="text-right font-bold text-red-600">{formatCurrency(expense.amount)}</TableCell>
                            <TableCell>
                              {hasBill ? (
                                <div className="flex items-center gap-1 flex-wrap">
                                  {expense.bill_number && (
                                    <span className="text-xs text-muted-foreground">{expense.bill_number}</span>
                                  )}
                                  {expenseBills.map((billUrl, idx) => (
                                    <div key={idx} className="flex items-center">
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        className="h-7 px-2"
                                        onClick={() => viewBill(billUrl)}
                                        title={`View Bill ${idx + 1}`}
                                        data-testid={`view-bill-${expense.id}-${idx}`}
                                      >
                                        <Eye className="h-4 w-4 text-blue-600" />
                                      </Button>
                                      <Button 
                                        size="sm" 
                                        variant="ghost"
                                        className="h-7 px-2"
                                        onClick={() => downloadBill(billUrl, `bill_${expense.id}_${idx + 1}`)}
                                        title={`Download Bill ${idx + 1}`}
                                      >
                                        <Download className="h-4 w-4 text-green-600" />
                                      </Button>
                                    </div>
                                  ))}
                                  {expenseBills.length > 1 && (
                                    <span className="text-xs text-muted-foreground">({expenseBills.length})</span>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-1 text-red-600">
                                  <AlertTriangle className="h-4 w-4" />
                                  <span className="text-xs">No bill</span>
                                </div>
                              )}
                            </TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1 items-start">
                                {getStatusBadge(expense.status)}
                                {expense.status === 'rejected' && expense.rejection_reason && (
                                  <span className="text-xs text-red-600 italic" data-testid={`approval-rejection-reason-${expense.id}`}>
                                    {expense.rejection_reason}
                                  </span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1 items-center">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 h-7 w-7 p-0"
                                  onClick={() => handleOpenEditExpense(expense)}
                                  title="Edit"
                                  data-testid={`edit-pending-expense-${expense.id}`}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                {expense.status !== 'approved' && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="text-green-600 hover:text-green-700 hover:bg-green-50 h-7 w-7 p-0"
                                    onClick={() => handleApproveExpense(expense)}
                                    title="Approve"
                                    data-testid={`approve-expense-${expense.id}`}
                                  >
                                    <CheckCircle className="h-4 w-4" />
                                  </Button>
                                )}
                                {expense.status !== 'rejected' && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="text-red-600 hover:text-red-700 hover:bg-red-50 h-7 w-7 p-0"
                                    onClick={() => {
                                      setSelectedExpense(expense);
                                      setApprovalDialogOpen(true);
                                    }}
                                    title="Reject"
                                    data-testid={`reject-expense-${expense.id}`}
                                  >
                                    <XCircle className="h-4 w-4" />
                                  </Button>
                                )}
                                {expense.status !== 'pending' && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost" 
                                    className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 h-7 w-7 p-0"
                                    onClick={() => handleRevertToPending(expense)}
                                    title="Move back to Pending"
                                    data-testid={`revert-expense-${expense.id}`}
                                  >
                                    <RotateCcw className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        )}
        
        {/* Reports Tab */}
        {isAdmin && (
          <TabsContent value="reports">
            <div className="space-y-6">
              {/* Date Range Filter */}
              <Card>
                <CardContent className="pt-6">
                  <div className="flex flex-wrap items-end gap-4">
                    <div className="space-y-2">
                      <Label>Start Date</Label>
                      <Input
                        type="date"
                        value={reportDateRange.start_date}
                        onChange={(e) => setReportDateRange({ ...reportDateRange, start_date: e.target.value })}
                        className="w-40"
                        data-testid="report-start-date"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>End Date</Label>
                      <Input
                        type="date"
                        value={reportDateRange.end_date}
                        onChange={(e) => setReportDateRange({ ...reportDateRange, end_date: e.target.value })}
                        className="w-40"
                        data-testid="report-end-date"
                      />
                    </div>
                    <Button onClick={fetchAnalytics} disabled={analyticsLoading} data-testid="apply-report-filter">
                      {analyticsLoading ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Loading...</>
                      ) : (
                        <>Apply Filter</>
                      )}
                    </Button>
                    {(reportDateRange.start_date || reportDateRange.end_date) && (
                      <Button 
                        variant="outline" 
                        onClick={() => {
                          setReportDateRange({ start_date: '', end_date: '' });
                          fetchAnalytics();
                        }}
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
              
              {analyticsLoading ? (
                <div className="flex items-center justify-center h-64">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : analytics ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2">
                          <IndianRupee className="h-4 w-4 text-primary" />
                          <span className="text-sm text-muted-foreground">Total Spent</span>
                        </div>
                        <p className="text-2xl font-bold mt-1">{formatCurrency(analytics.summary.total_expenses)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2">
                          <Receipt className="h-4 w-4 text-blue-600" />
                          <span className="text-sm text-muted-foreground">Expenses</span>
                        </div>
                        <p className="text-2xl font-bold mt-1">{analytics.summary.expense_count}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2">
                          <TrendingUp className="h-4 w-4 text-green-600" />
                          <span className="text-sm text-muted-foreground">Average</span>
                        </div>
                        <p className="text-2xl font-bold mt-1">{formatCurrency(analytics.summary.average_expense)}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2">
                          <Clock className="h-4 w-4 text-amber-600" />
                          <span className="text-sm text-muted-foreground">Pending</span>
                        </div>
                        <p className="text-2xl font-bold mt-1">{analytics.summary.pending_count}</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardContent className="pt-4">
                        <div className="flex items-center gap-2">
                          <AlertTriangle className="h-4 w-4 text-red-600" />
                          <span className="text-sm text-muted-foreground">Pending Amt</span>
                        </div>
                        <p className="text-2xl font-bold mt-1">{formatCurrency(analytics.summary.pending_amount)}</p>
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Charts Row 1 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Monthly Trend Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Calendar className="h-5 w-5" />
                          Monthly Expense Trend
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {analytics.by_month.length > 0 ? (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={analytics.by_month}>
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                              <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                              <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                              <Bar dataKey="amount" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-64 text-muted-foreground">
                            No data available
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* Category Distribution Pie Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Receipt className="h-5 w-5" />
                          Expenses by Category
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {analytics.by_category.length > 0 ? (
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={analytics.by_category}
                                cx="50%"
                                cy="50%"
                                labelLine={false}
                                label={({ category, percent }) => `${category.substring(0, 10)}${category.length > 10 ? '...' : ''} (${(percent * 100).toFixed(0)}%)`}
                                outerRadius={100}
                                fill="#8884d8"
                                dataKey="amount"
                              >
                                {analytics.by_category.map((entry, index) => (
                                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                ))}
                              </Pie>
                              <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                            </PieChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-64 text-muted-foreground">
                            No data available
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Charts Row 2 */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* By Property Bar Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Building2 className="h-5 w-5" />
                          Expenses by Property
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {analytics.by_property.length > 0 ? (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={analytics.by_property} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                              <YAxis dataKey="property" type="category" tick={{ fontSize: 12 }} width={100} />
                              <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                              <Bar dataKey="amount" fill="#06b6d4" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-64 text-muted-foreground">
                            No data available
                          </div>
                        )}
                      </CardContent>
                    </Card>
                    
                    {/* By User Bar Chart */}
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Users className="h-5 w-5" />
                          Expenses by User
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {analytics.by_user.length > 0 ? (
                          <ResponsiveContainer width="100%" height={300}>
                            <BarChart data={analytics.by_user.slice(0, 8)} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" />
                              <XAxis type="number" tick={{ fontSize: 12 }} tickFormatter={(v) => `₹${(v/1000).toFixed(0)}k`} />
                              <YAxis dataKey="user" type="category" tick={{ fontSize: 12 }} width={100} />
                              <Tooltip formatter={(value) => [formatCurrency(value), 'Amount']} />
                              <Bar dataKey="amount" fill="#10b981" radius={[0, 4, 4, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        ) : (
                          <div className="flex items-center justify-center h-64 text-muted-foreground">
                            No data available
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                  
                  {/* Top Vendors Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Truck className="h-5 w-5" />
                        Top Vendors by Expense
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.by_vendor.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Vendor</TableHead>
                              <TableHead className="text-center">Transactions</TableHead>
                              <TableHead className="text-right">Total Amount</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {analytics.by_vendor.map((vendor, index) => (
                              <TableRow key={index}>
                                <TableCell className="font-medium">{vendor.vendor}</TableCell>
                                <TableCell className="text-center">{vendor.count}</TableCell>
                                <TableCell className="text-right font-semibold">{formatCurrency(vendor.amount)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="flex items-center justify-center h-32 text-muted-foreground">
                          No vendor data available
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  
                  {/* Category Breakdown Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Category Breakdown</CardTitle>
                    </CardHeader>
                    <CardContent>
                      {analytics.by_category.length > 0 ? (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Category</TableHead>
                              <TableHead className="text-center">Count</TableHead>
                              <TableHead className="text-right">Amount</TableHead>
                              <TableHead className="text-right">% of Total</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {analytics.by_category.map((cat, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  <div className="flex items-center gap-2">
                                    <div 
                                      className="w-3 h-3 rounded-full" 
                                      style={{ backgroundColor: CHART_COLORS[index % CHART_COLORS.length] }}
                                    />
                                    {cat.category}
                                  </div>
                                </TableCell>
                                <TableCell className="text-center">{cat.count}</TableCell>
                                <TableCell className="text-right font-semibold">{formatCurrency(cat.amount)}</TableCell>
                                <TableCell className="text-right text-muted-foreground">
                                  {((cat.amount / analytics.summary.total_expenses) * 100).toFixed(1)}%
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      ) : (
                        <div className="flex items-center justify-center h-32 text-muted-foreground">
                          No category data available
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <BarChart3 className="h-12 w-12 text-muted-foreground mb-4" />
                    <p className="text-muted-foreground text-center">
                      Click the Reports tab to load analytics data
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        )}
      </Tabs>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              Delete Petty Cash Entry
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this entry? This action can be undone by restoring from the deleted entries list.
            </DialogDescription>
          </DialogHeader>
          {entryToDelete && (
            <div className="py-4">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Type:</span>
                  <span className="font-medium capitalize">{entryToDelete.type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Amount:</span>
                  <span className="font-medium text-red-600">{formatCurrency(entryToDelete.amount)}</span>
                </div>
                {entryToDelete.description && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Description:</span>
                    <span className="font-medium">{entryToDelete.description}</span>
                  </div>
                )}
                {entryToDelete.category && (
                  <div className="flex justify-between">
                    <span className="text-sm text-muted-foreground">Category:</span>
                    <Badge variant="outline">{entryToDelete.category}</Badge>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Date:</span>
                  <span className="font-medium">{formatDate(entryToDelete.created_at)}</span>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} disabled={deleting}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleConfirmDelete}
              disabled={deleting}
              data-testid="confirm-delete-btn"
            >
              {deleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Entry
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-primary" />
              Transfer Petty Cash
            </DialogTitle>
            <DialogDescription>Transfer cash to a team member</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Show available balance for non-admin users */}
            {!isAdmin && (
              <div className="bg-muted/50 p-4 rounded-lg">
                <p className="text-sm text-muted-foreground">Your Available Balance</p>
                <p className="text-2xl font-bold text-primary">{formatCurrency(myBalance?.available_balance || 0)}</p>
              </div>
            )}
            {isAdmin && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                <p className="text-sm text-blue-700">
                  As admin, you can transfer petty cash to team members from the central fund.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Recipient *</Label>
              <Select value={transferForm.recipient_user_id} onValueChange={(v) => setTransferForm({ ...transferForm, recipient_user_id: v })}>
                <SelectTrigger data-testid="transfer-recipient-select">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount (₹) *</Label>
              <Input
                type="number"
                min="0"
                max={!isAdmin ? myBalance?.available_balance : undefined}
                step="0.01"
                value={transferForm.amount}
                onChange={(e) => setTransferForm({ ...transferForm, amount: e.target.value })}
                placeholder="Enter amount"
                data-testid="transfer-amount-input"
              />
              {!isAdmin && transferForm.amount && parseFloat(transferForm.amount) > (myBalance?.available_balance || 0) && (
                <p className="text-xs text-red-600">Amount exceeds your available balance</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Transfer Date *</Label>
              <Input
                type="date"
                value={transferForm.transfer_date}
                onChange={(e) => setTransferForm({ ...transferForm, transfer_date: e.target.value })}
                data-testid="transfer-date-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes <span className="text-xs text-muted-foreground">(spell-check)</span></Label>
              <SpellCheckInput
                value={transferForm.notes}
                onChange={(e) => setTransferForm({ ...transferForm, notes: e.target.value })}
                placeholder="Add any notes..."
                context="transfer notes"
                multiline
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateTransfer} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Expense Dialog */}
      <Dialog open={expenseDialogOpen} onOpenChange={setExpenseDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-primary" />
              Record Expense
            </DialogTitle>
            <DialogDescription>Record a petty cash expense for approval</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Row 1: Amount, Date, Property */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  min="0"
                  step="0.01"
                  value={expenseForm.amount}
                  onChange={(e) => setExpenseForm({ ...expenseForm, amount: e.target.value })}
                  placeholder="Enter amount"
                  data-testid="expense-amount-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={expenseForm.expense_date}
                  onChange={(e) => setExpenseForm({ ...expenseForm, expense_date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Building2 className="h-3 w-3" /> Property</Label>
                <Select value={expenseForm.property_id} onValueChange={(v) => setExpenseForm({ ...expenseForm, property_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="expense-property-select">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Property</SelectItem>
                    {properties.map(prop => (
                      <SelectItem key={prop.id} value={prop.id}>{prop.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Row 2: Description */}
            <div className="space-y-2">
              <Label>Description * <span className="text-xs text-muted-foreground">(AI spell-check enabled)</span></Label>
              <SpellCheckInput
                value={expenseForm.description}
                onChange={(e) => setExpenseForm({ ...expenseForm, description: e.target.value })}
                placeholder="What was this expense for?"
                context="expense description"
                data-testid="expense-description-input"
              />
            </div>
            
            {/* Row 3: Category, Other Category (if selected) */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={expenseForm.category} onValueChange={(v) => setExpenseForm({ ...expenseForm, category: v, other_category: '' })}>
                  <SelectTrigger data-testid="expense-category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {expenseForm.category === 'Other' && (
                <div className="space-y-2">
                  <Label>Specify Category *</Label>
                  <Input
                    value={expenseForm.other_category}
                    onChange={(e) => setExpenseForm({ ...expenseForm, other_category: e.target.value })}
                    placeholder="Enter custom category"
                    data-testid="expense-other-category-input"
                  />
                </div>
              )}
            </div>
            
            {/* Row 4: Vendor, PO Link */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Truck className="h-3 w-3" /> Vendor</Label>
                <Select value={expenseForm.vendor_id} onValueChange={(v) => setExpenseForm({ ...expenseForm, vendor_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="expense-vendor-select">
                    <SelectValue placeholder="Select vendor (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Vendor</SelectItem>
                    {vendors.map(vendor => (
                      <SelectItem key={vendor.id} value={vendor.id}>{vendor.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Link to PO</Label>
                <Select value={expenseForm.po_id} onValueChange={(v) => setExpenseForm({ ...expenseForm, po_id: v === 'none' ? '' : v, client_id: '' })}>
                  <SelectTrigger data-testid="expense-po-select">
                    <SelectValue placeholder="Select PO (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No PO Link</SelectItem>
                    {purchaseOrders.map(po => (
                      <SelectItem key={po.id} value={po.id}>{po.po_number} - {po.vendor_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {/* Row 4b: Client Selection (shown when no PO is linked) */}
            {!expenseForm.po_id && (
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Users className="h-3 w-3" /> Link to Client</Label>
                <Select value={expenseForm.client_id} onValueChange={(v) => setExpenseForm({ ...expenseForm, client_id: v === 'none' ? '' : v })}>
                  <SelectTrigger data-testid="expense-client-select">
                    <SelectValue placeholder="Select Client (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Client Link</SelectItem>
                    {clients.map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.client_name} {client.event_type ? `(${client.event_type})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Link this expense to a client event if not linked to a PO</p>
              </div>
            )}
            
            {/* Row 5: Purchased By */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><User className="h-3 w-3" /> Purchased By <span className="text-xs text-muted-foreground">(spell-check)</span></Label>
              <SpellCheckInput
                value={expenseForm.purchased_by}
                onChange={(e) => setExpenseForm({ ...expenseForm, purchased_by: e.target.value })}
                placeholder="Name of person who made the purchase"
                context="person name"
                data-testid="expense-purchased-by-input"
              />
            </div>
            
            {/* Row 6: Bill Number, Bill Upload */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bill Number</Label>
                <Input
                  value={expenseForm.bill_number}
                  onChange={(e) => setExpenseForm({ ...expenseForm, bill_number: e.target.value })}
                  placeholder="Bill/Receipt number"
                />
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1"><Upload className="h-3 w-3" /> Upload Bills</Label>
                <div className="flex gap-2">
                  <Input
                    type="file"
                    ref={fileInputRef}
                    accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                    multiple
                    onChange={handleBillUpload}
                    className="hidden"
                  />
                  <Button 
                    type="button" 
                    variant="outline" 
                    className="w-full"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                    ) : expenseForm.bill_images.length > 0 ? (
                      <><Plus className="h-4 w-4 mr-2" />Add More Bills ({expenseForm.bill_images.length} uploaded)</>
                    ) : (
                      <><Upload className="h-4 w-4 mr-2" />Choose Files</>
                    )}
                  </Button>
                </div>
                {/* Display uploaded bills */}
                {expenseForm.bill_images.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {expenseForm.bill_images.map((billUrl, index) => (
                      <div key={index} className="flex items-center gap-1 bg-muted px-2 py-1 rounded text-sm">
                        <FileText className="h-3 w-3" />
                        <span>Bill {index + 1}</span>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          className="h-5 w-5"
                          onClick={() => viewBill(billUrl)}
                          title="View Bill"
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          className="h-5 w-5 text-green-600"
                          onClick={() => downloadBill(billUrl, `bill_${index + 1}`)}
                          title="Download Bill"
                        >
                          <Download className="h-3 w-3" />
                        </Button>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="icon"
                          className="h-5 w-5 text-red-500 hover:text-red-700"
                          onClick={() => handleRemoveBill(index)}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                {expenseForm.bill_images.length === 0 && !expenseForm.bill_number && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Expenses without bills will be highlighted for review
                  </p>
                )}
              </div>
            </div>
            
            {/* Row 7: Notes */}
            <div className="space-y-2">
              <Label>Notes <span className="text-xs text-muted-foreground">(AI spell-check enabled)</span></Label>
              <SpellCheckInput
                value={expenseForm.notes}
                onChange={(e) => setExpenseForm({ ...expenseForm, notes: e.target.value })}
                placeholder="Additional notes..."
                context="expense notes"
                multiline
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExpenseDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateExpense} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Submit for Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Return Dialog */}
      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="h-5 w-5 text-primary" />
              Return Petty Cash
            </DialogTitle>
            <DialogDescription>Return unused petty cash</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm text-muted-foreground">Your Available Balance</p>
              <p className="text-2xl font-bold text-primary">{formatCurrency(myBalance?.available_balance || 0)}</p>
            </div>
            <div className="space-y-2">
              <Label>Amount to Return (₹) *</Label>
              <Input
                type="number"
                min="0"
                max={myBalance?.available_balance || 0}
                step="0.01"
                value={returnForm.amount}
                onChange={(e) => setReturnForm({ ...returnForm, amount: e.target.value })}
                placeholder="Enter amount"
                data-testid="return-amount-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Notes <span className="text-xs text-muted-foreground">(spell-check)</span></Label>
              <SpellCheckInput
                value={returnForm.notes}
                onChange={(e) => setReturnForm({ ...returnForm, notes: e.target.value })}
                placeholder="Add any notes..."
                context="return notes"
                multiline
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleCreateReturn} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Return Cash
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog open={approvalDialogOpen} onOpenChange={setApprovalDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="h-5 w-5" />
              Reject Expense
            </DialogTitle>
            <DialogDescription>
              Rejecting expense from {selectedExpense?.user_name}: {formatCurrency(selectedExpense?.amount)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rejection Reason *</Label>
              <Textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Please provide a reason for rejection..."
                data-testid="rejection-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setApprovalDialogOpen(false);
              setSelectedExpense(null);
              setRejectionReason('');
            }}>Cancel</Button>
            <Button variant="destructive" onClick={() => handleRejectExpense(selectedExpense)} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Reject
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Report Dialog */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5" />
              Export Expense Report
            </DialogTitle>
            <DialogDescription>
              Generate a PDF report of petty cash expenses with optional filters
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Date Range */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={reportFilters.start_date}
                  onChange={(e) => setReportFilters({ ...reportFilters, start_date: e.target.value })}
                  data-testid="report-start-date"
                />
              </div>
              <div className="space-y-2">
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={reportFilters.end_date}
                  onChange={(e) => setReportFilters({ ...reportFilters, end_date: e.target.value })}
                  data-testid="report-end-date"
                />
              </div>
            </div>
            
            {/* User Filter */}
            <div className="space-y-2">
              <Label>User</Label>
              <Select value={reportFilters.user_id} onValueChange={(v) => setReportFilters({ ...reportFilters, user_id: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="report-user-select">
                  <SelectValue placeholder="All Users" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Client Filter */}
            <div className="space-y-2">
              <Label>Client</Label>
              <Select value={reportFilters.client_id} onValueChange={(v) => setReportFilters({ ...reportFilters, client_id: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="report-client-select">
                  <SelectValue placeholder="All Clients" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Clients</SelectItem>
                  {clients.map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.client_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Category Filter */}
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={reportFilters.category} onValueChange={(v) => setReportFilters({ ...reportFilters, category: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="report-category-select">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {expenseCategories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Status Filter */}
            <div className="space-y-2">
              <Label>Status</Label>
              <Select value={reportFilters.status} onValueChange={(v) => setReportFilters({ ...reportFilters, status: v === 'all' ? '' : v })}>
                <SelectTrigger data-testid="report-status-select">
                  <SelectValue placeholder="All Statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDownloadReport} disabled={downloadingPdf} data-testid="download-report-btn">
              {downloadingPdf ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
              ) : (
                <><Download className="h-4 w-4 mr-2" />Download PDF</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Expense Dialog */}
      <Dialog open={editExpenseDialogOpen} onOpenChange={setEditExpenseDialogOpen}>
        <DialogContent className="!w-[90vw] !max-w-[900px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit Expense
            </DialogTitle>
            <DialogDescription>
              Update expense details. Changes will be saved immediately.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Amount (₹) *</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editExpenseForm.amount}
                  onChange={(e) => setEditExpenseForm({ ...editExpenseForm, amount: e.target.value })}
                  data-testid="edit-expense-amount"
                />
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input
                  type="date"
                  value={editExpenseForm.expense_date}
                  onChange={(e) => setEditExpenseForm({ ...editExpenseForm, expense_date: e.target.value })}
                  data-testid="edit-expense-date"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Description *</Label>
              <Input
                value={editExpenseForm.description}
                onChange={(e) => setEditExpenseForm({ ...editExpenseForm, description: e.target.value })}
                placeholder="What was purchased?"
                data-testid="edit-expense-description"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select
                  value={editExpenseForm.category}
                  onValueChange={(v) => setEditExpenseForm({ ...editExpenseForm, category: v })}
                >
                  <SelectTrigger data-testid="edit-expense-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {expenseCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editExpenseForm.category === 'Other' && (
                <div className="space-y-2">
                  <Label>Specify Other Category</Label>
                  <Input
                    value={editExpenseForm.other_category}
                    onChange={(e) => setEditExpenseForm({ ...editExpenseForm, other_category: e.target.value })}
                    placeholder="Enter category"
                  />
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Property</Label>
                <Select
                  value={editExpenseForm.property_id || "none"}
                  onValueChange={(v) => setEditExpenseForm({ ...editExpenseForm, property_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="edit-expense-property">
                    <SelectValue placeholder="Select property" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Property</SelectItem>
                    {properties.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Vendor</Label>
                <Select
                  value={editExpenseForm.vendor_id || "none"}
                  onValueChange={(v) => setEditExpenseForm({ ...editExpenseForm, vendor_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger data-testid="edit-expense-vendor">
                    <SelectValue placeholder="Select vendor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Vendor</SelectItem>
                    {vendors.map(v => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Link to PO</Label>
                <Select
                  value={editExpenseForm.po_id || "none"}
                  onValueChange={(v) => setEditExpenseForm({ ...editExpenseForm, po_id: v === "none" ? "" : v, client_id: v !== "none" ? "" : editExpenseForm.client_id })}
                >
                  <SelectTrigger data-testid="edit-expense-po">
                    <SelectValue placeholder="Select PO (optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No PO</SelectItem>
                    {purchaseOrders.map(po => (
                      <SelectItem key={po.id} value={po.id}>{po.po_number} - {po.vendor_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {!editExpenseForm.po_id && (
                <div className="space-y-2">
                  <Label>Link to Client</Label>
                  <Select
                    value={editExpenseForm.client_id || "none"}
                    onValueChange={(v) => setEditExpenseForm({ ...editExpenseForm, client_id: v === "none" ? "" : v })}
                  >
                    <SelectTrigger data-testid="edit-expense-client">
                      <SelectValue placeholder="Select client (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Client</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bill Number</Label>
                <Input
                  value={editExpenseForm.bill_number}
                  onChange={(e) => setEditExpenseForm({ ...editExpenseForm, bill_number: e.target.value })}
                  placeholder="Invoice/Bill number"
                  data-testid="edit-expense-bill-number"
                />
              </div>
              <div className="space-y-2">
                <Label>Purchased By</Label>
                <Input
                  value={editExpenseForm.purchased_by}
                  onChange={(e) => setEditExpenseForm({ ...editExpenseForm, purchased_by: e.target.value })}
                  placeholder="Person who made the purchase"
                  data-testid="edit-expense-purchased-by"
                />
              </div>
            </div>
            
            {/* Bill Images Upload Section */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1"><Upload className="h-3 w-3" /> Bills</Label>
              <div className="flex gap-2">
                <Input
                  type="file"
                  ref={editBillInputRef}
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.csv"
                  multiple
                  onChange={handleEditBillUpload}
                  className="hidden"
                />
                <Button 
                  type="button" 
                  variant="outline" 
                  className="w-full"
                  onClick={() => editBillInputRef.current?.click()}
                  disabled={uploading}
                >
                  {uploading ? (
                    <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</>
                  ) : editExpenseForm.bill_images.length > 0 ? (
                    <><Plus className="h-4 w-4 mr-2" />Add More Bills ({editExpenseForm.bill_images.length} attached)</>
                  ) : (
                    <><Upload className="h-4 w-4 mr-2" />Upload Bills</>
                  )}
                </Button>
              </div>
              {/* Display uploaded bills */}
              {editExpenseForm.bill_images.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {editExpenseForm.bill_images.map((billUrl, index) => (
                    <div key={index} className="flex items-center gap-1 bg-muted px-2 py-1 rounded text-sm">
                      <FileText className="h-3 w-3" />
                      <span>Bill {index + 1}</span>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        className="h-5 w-5"
                        onClick={() => viewBill(billUrl)}
                        title="View Bill"
                      >
                        <Eye className="h-3 w-3" />
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        className="h-5 w-5 text-green-600"
                        onClick={() => downloadBill(billUrl, `bill_${index + 1}`)}
                        title="Download Bill"
                      >
                        <Download className="h-3 w-3" />
                      </Button>
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="icon"
                        className="h-5 w-5 text-red-500 hover:text-red-700"
                        onClick={() => handleRemoveEditBill(index)}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={editExpenseForm.notes}
                onChange={(e) => setEditExpenseForm({ ...editExpenseForm, notes: e.target.value })}
                rows={2}
                placeholder="Additional notes..."
                data-testid="edit-expense-notes"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditExpenseDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateExpense} disabled={saving} data-testid="save-edit-expense-btn">
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <>Save Changes</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Transfer Dialog */}
      <Dialog open={editTransferDialogOpen} onOpenChange={setEditTransferDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="h-5 w-5" />
              Edit Transfer Entry
            </DialogTitle>
            <DialogDescription>
              Correct transfer details. This will update the transaction record.
            </DialogDescription>
          </DialogHeader>

          {editingTransfer && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
                <p><strong>Original Transfer:</strong></p>
                <p>From: {editingTransfer.from_user_name}</p>
                <p>To: {editingTransfer.to_user_name}</p>
                <p>Amount: {formatCurrency(editingTransfer.amount)}</p>
                <p>Date: {formatDate(editingTransfer.created_at)}</p>
              </div>

              <div className="space-y-4">
                <div>
                  <Label>Transfer To (Recipient) *</Label>
                  <Select 
                    value={editTransferForm.recipient_user_id} 
                    onValueChange={(value) => setEditTransferForm({...editTransferForm, recipient_user_id: value})}
                  >
                    <SelectTrigger data-testid="edit-transfer-recipient">
                      <SelectValue placeholder="Select recipient" />
                    </SelectTrigger>
                    <SelectContent>
                      {users.map(user => (
                        <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Amount (₹) *</Label>
                  <Input 
                    type="number"
                    value={editTransferForm.amount}
                    onChange={(e) => setEditTransferForm({...editTransferForm, amount: e.target.value})}
                    placeholder="Enter amount"
                    data-testid="edit-transfer-amount"
                  />
                </div>

                <div>
                  <Label>Notes</Label>
                  <Textarea 
                    value={editTransferForm.notes}
                    onChange={(e) => setEditTransferForm({...editTransferForm, notes: e.target.value})}
                    placeholder="Optional notes"
                    rows={2}
                    data-testid="edit-transfer-notes"
                  />
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTransferDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdateTransfer} disabled={saving} data-testid="save-edit-transfer-btn">
              {saving ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <>Save Changes</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
        <DialogContent className="!w-[90vw] !max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Upload className="h-5 w-5" />Bulk Transfer Upload</DialogTitle>
            <DialogDescription>Upload an Excel file with Date and Amount columns to create multiple transfers at once.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Recipient *</Label>
              <Select value={bulkRecipient} onValueChange={setBulkRecipient}>
                <SelectTrigger data-testid="bulk-recipient-select">
                  <SelectValue placeholder="Select team member" />
                </SelectTrigger>
                <SelectContent>
                  {users.map(user => (
                    <SelectItem key={user.id} value={user.id}>{user.full_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Excel File (.xlsx, .xls, .csv) *</Label>
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setBulkFile(e.target.files?.[0] || null)} data-testid="bulk-file-input" />
              <p className="text-xs text-muted-foreground">File must have columns: <strong>Date</strong> (dd/mm/yy or dd/mm/yyyy) and <strong>Amount</strong></p>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input value={bulkNotes} onChange={(e) => setBulkNotes(e.target.value)} placeholder="Optional note for all transfers" />
            </div>

            {/* Results */}
            {bulkResult && (
              <div className="space-y-2">
                <div className={`p-3 rounded-lg ${bulkResult.error_count > 0 ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
                  <p className="text-sm font-medium">{bulkResult.message}</p>
                  {bulkResult.error_count > 0 && <p className="text-xs text-amber-700 mt-1">{bulkResult.error_count} row(s) had errors</p>}
                </div>
                {bulkResult.created?.length > 0 && (
                  <div className="max-h-40 overflow-y-auto text-xs space-y-1">
                    {bulkResult.created.map((c, i) => (
                      <div key={i} className="flex justify-between bg-emerald-50 px-2 py-1 rounded">
                        <span>{new Date(c.date).toLocaleDateString('en-IN')}</span>
                        <span className="font-medium">₹{c.amount.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
                {bulkResult.errors?.length > 0 && (
                  <div className="max-h-32 overflow-y-auto text-xs space-y-1">
                    {bulkResult.errors.map((e, i) => (
                      <p key={i} className="text-red-600">{e}</p>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setBulkUploadOpen(false)}>Close</Button>
            {bulkResult && bulkResult.batch_id && bulkResult.created_count > 0 && (
              <Button variant="destructive" onClick={handleBulkUndo} data-testid="bulk-undo-btn">
                <Trash2 className="h-4 w-4 mr-2" />Undo This Upload ({bulkResult.created_count} transfers)
              </Button>
            )}
            {!bulkResult && (
              <Button onClick={handleBulkUpload} disabled={bulkUploading} data-testid="bulk-upload-submit">
                {bulkUploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</> : <><Upload className="h-4 w-4 mr-2" />Upload & Create</>}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Member Transfers Dialog */}
      <Dialog open={memberTransfersOpen} onOpenChange={(open) => { setMemberTransfersOpen(open); if (!open) { setDeleteTransferId(null); setSelectedTransferIds(new Set()); } }}>
        <DialogContent className="!w-[90vw] !max-w-[700px]">
          <DialogHeader>
            <DialogTitle>{memberTransfers?.user_name ? `Transfers to ${memberTransfers.user_name}` : 'Transfer Details'}</DialogTitle>
            <DialogDescription>
              {memberTransfers ? `${memberTransfers.count} transfer(s) — Total: ${formatCurrency(memberTransfers.total_amount)}` : 'Loading...'}
            </DialogDescription>
          </DialogHeader>

          {/* Bulk actions bar */}
          {selectedTransferIds.size > 0 && (
            <div className="flex items-center justify-between bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              <span className="text-sm font-medium text-red-700">{selectedTransferIds.size} selected</span>
              <Button size="sm" variant="destructive" onClick={handleBulkDeleteTransfers} disabled={bulkDeleting} data-testid="bulk-delete-selected-btn">
                {bulkDeleting ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Trash2 className="h-4 w-4 mr-1" />}
                Delete Selected
              </Button>
            </div>
          )}

          <div className="max-h-[60vh] overflow-y-auto">
            {memberTransfersLoading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin" /></div>
            ) : memberTransfers?.transfers?.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[40px]">
                      <input type="checkbox" className="h-4 w-4 rounded border-slate-300 cursor-pointer"
                        checked={memberTransfers.transfers.length > 0 && selectedTransferIds.size === memberTransfers.transfers.length}
                        onChange={toggleSelectAll} data-testid="select-all-checkbox" />
                    </TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead className="text-center w-[60px]">Delete</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {memberTransfers.transfers.map(t => (
                    <TableRow key={t.id} className={selectedTransferIds.has(t.id) ? 'bg-red-50' : ''}>
                      <TableCell>
                        <input type="checkbox" className="h-4 w-4 rounded border-slate-300 cursor-pointer"
                          checked={selectedTransferIds.has(t.id)} onChange={() => toggleTransferSelection(t.id)}
                          data-testid={`checkbox-${t.id}`} />
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-sm">
                        {t.created_at ? new Date(t.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                      </TableCell>
                      <TableCell className="text-right font-bold text-blue-600">{formatCurrency(t.amount)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{t.from_user_name}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">{t.notes || '—'}</TableCell>
                      <TableCell className="text-center">
                        <Button size="sm" variant="ghost" className="h-7 text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => handleDeleteTransfer(t.id)} data-testid={`delete-transfer-${t.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center text-muted-foreground py-6">No transfers found</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMemberTransfersOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PettyCashPage;
